import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Bell, Menu, X, LogOut, Home, UserCircle } from 'lucide-react';
import './Dashboard.css';
import logo from '../components/assets/logo.png';
import { useNavigate } from "react-router-dom";
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Local Storage Helper
const LocalStorageHelper = {
  saveLoginData: (userId, email, name, userType = 'user') => {
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userId', userId);
    localStorage.setItem('userEmail', email);
    localStorage.setItem('userName', name);
    localStorage.setItem('userType', userType);
  },

  isLoggedIn: () => {
    return localStorage.getItem('isLoggedIn') === 'true';
  },

  getLoginData: () => {
    return {
      userId: localStorage.getItem('userId'),
      email: localStorage.getItem('userEmail'),
      name: localStorage.getItem('userName'),
      userType: localStorage.getItem('userType'),
    };
  },

  clearLoginData: () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('userType');
  }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // NEW: Authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [photographerData, setPhotographerData] = useState(null);
  const [photographerName, setPhotographerName] = useState('Photographer');
  const [profilePhoto, setProfilePhoto] = useState(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile && !sidebarVisible) {
        setSidebarVisible(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarVisible]);

  // NEW: Authentication check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        await loadPhotographerData(user.uid);
        await loadProfilePhoto(user.uid);
      } else {
        navigate('/Login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // NEW: Load photographer data to get name
  const loadPhotographerData = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'photographers', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setPhotographerData(userData);
        setPhotographerName(userData.name || userData.displayName || 'Photographer');
      } else {
        // Fallback to auth user data
        if (auth.currentUser) {
          setPhotographerName(auth.currentUser.displayName || 'Photographer');
        }
      }
    } catch (error) {
      console.error('Error loading photographer data:', error);
      // Fallback to auth user data
      if (auth.currentUser) {
        setPhotographerName(auth.currentUser.displayName || 'Photographer');
      }
    }
  };

  // NEW: Load profile photo
  const loadProfilePhoto = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'photographers', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const profilePhotoUrl = userData.profileImage || null;
        setProfilePhoto(profilePhotoUrl);
      }
    } catch (error) {
      console.error('Error loading profile photo:', error);
    }
  };

  // NEW: Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      LocalStorageHelper.clearLoginData();
      navigate('/Login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // NEW: Handle home navigation
  const handleHomeNavigation = () => {
    navigate('/LandingPage');
    closeSidebar();
  };

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  };

  const chartData = [
    { month: 'Jan', value: 45000 },
    { month: 'Feb', value: 42000 },
    { month: 'Mar', value: 44000 },
    { month: 'Apr', value: 54600 },
    { month: 'May', value: 48000 },
    { month: 'Jun', value: 54600 },
  ];

  const notifications = [
    { id: 1, message: 'A new user Viewed your Profile', time: '10 mins ago' },
    { id: 2, message: 'You have a new profile rating', time: '2 hours ago' },
    { id: 3, message: 'You have a new profile review', time: '1 day ago' },
  ];

  return (
    <div className="dashboard-container">
      {/* Toggle Button */}
      <button
        className={`dashboard-toggle-btn ${!sidebarVisible ? 'active' : ''}`}
        onClick={toggleSidebar}
      >
        <div className="dashboard-toggle-icon">
          {sidebarVisible ? <Menu size={20} /> : <X size={20} />}
        </div>
      </button>

      {/* Overlay for mobile */}
      {isMobile && sidebarVisible && (
        <div className="dashboard-overlay active" onClick={closeSidebar}></div>
      )}

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isMobile
          ? (sidebarVisible ? 'show' : '')
          : (sidebarVisible ? '' : 'hidden')
        }`}>
        <img src={logo} alt="LitStudio" className="dashboard-logo" />
        <nav>
          <ul className="dashboard-nav">
            <li className="dashboard-nav-item dashboard-nav-item--active">
               Dashboard
            </li>
            <li className="dashboard-nav-item" onClick={() => {
              navigate("/Upload");
              closeSidebar();
            }}>
               Upload
            </li>
            <li className="dashboard-nav-item" onClick={() => {
              navigate("/PhotographerRequests");
              closeSidebar();
            }}>
             Requests
            </li>
            <li className="dashboard-nav-item" onClick={() => {
              navigate("/Reviews");
              closeSidebar();
            }}>
               Reviews
            </li>
            {/* <li className="dashboard-nav-item" onClick={() => {
              navigate("/MemberShip");
              closeSidebar();
            }}>
               Membership Plan
            </li> */}
            <li className="dashboard-nav-item" onClick={() => {
              navigate("/PhotographProfile");
              closeSidebar();
            }}>
              Profile
            </li>
          </ul>
        </nav>

        {/* UPDATED: User Profile Section with Photographer Name and Navigation Buttons */}
        <div className="dashboard-user-section">
          <div className="dashboard-user-info">
            <div className="dashboard-user-avatar">
              {profilePhoto ? (
                <img
                  src={profilePhoto}
                  alt="Profile"
                  className="dashboard-user-avatar-image"
                />
              ) : (
                <UserCircle size={24} />
              )}
            </div>
            <div className="dashboard-welcome-text">
              <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '2px' }}>
                {photographerName}
              </div>
            </div>
          </div>

          {/* NEW: Navigation Buttons */}
          <div className="dashboard-user-actions">
            <button
              className="dashboard-user-action-btn dashboard-home-btn"
              onClick={handleHomeNavigation}
              title="Go to Home"
            >
              <Home size={16} />
              <span>Home</span>
            </button>
            <button
              className="dashboard-user-action-btn dashboard-logout-btn"
              onClick={handleLogout}
              title="Logout"
            >
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`dashboard-main ${!sidebarVisible && !isMobile ? 'expanded' : ''
        }`}>
        <h1 className="dashboard-header">Welcome Back, {photographerName}!</h1>

        <section className="dashboard-gallery-insights">
          <h2 className="dashboard-section-title">Gallery Insights</h2>
          <div className="dashboard-stats-grid">
            <div className="dashboard-stat-card">
              <h3 style={{ fontSize: '0.875rem', margin: '0 0 0.5rem 0', fontWeight: '500' }}>Albums Delivered</h3>
              <div className="dashboard-stat-value">1</div>
            </div>
            <div className="dashboard-stat-card">
              <h3 style={{ fontSize: '0.875rem', margin: '0 0 0.5rem 0', fontWeight: '500' }}>Photos Delivered</h3>
              <div className="dashboard-stat-value">800</div>
            </div>
            <div className="dashboard-stat-card">
              <h3 style={{ fontSize: '0.875rem', margin: '0 0 0.5rem 0', fontWeight: '500' }}>Pending Projects</h3>
              <div className="dashboard-stat-value">2</div>
            </div>
          </div>
        </section>

        <section className="dashboard-chart-container">
          <div className="dashboard-chart-header">
            <div>
              <h2 className="dashboard-section-title">Total Revenue</h2>
              <div className="dashboard-revenue-amount">₹11,</div>
            </div>
            <select className="dashboard-period-selector">
              <option>Week</option>
              <option>Month</option>
              <option>Year</option>
            </select>
          </div>
          <div className="dashboard-chart">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData}>
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#666' }}
                />
                <YAxis hide />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#ff4d4d"
                  strokeWidth={3}
                  dot={{
                    fill: '#fff',
                    stroke: '#ff4d4d',
                    strokeWidth: 2,
                    r: 4
                  }}
                  activeDot={{
                    r: 6,
                    fill: '#ff4d4d',
                    stroke: '#fff',
                    strokeWidth: 2
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <div className="dashboard-bottom-grid">
          <section className="dashboard-insights-section">
            <h2 className="dashboard-section-title">Your Insights</h2>
            <div className="dashboard-insights-grid">
              <div className="dashboard-insight-card">
                <h3 style={{ fontSize: '0.875rem', margin: '0 0 0.5rem 0', fontWeight: '500' }}>Profile Visits</h3>
                <div className="dashboard-insight-value" style={{ fontSize: '1.5rem', fontWeight: '700' }}>18</div>
              </div>
              <div className="dashboard-insight-card">
                <h3 style={{ fontSize: '0.875rem', margin: '0 0 0.5rem 0', fontWeight: '500' }}>Reviews</h3>
                <div className="dashboard-insight-value" style={{ fontSize: '1.5rem', fontWeight: '700' }}>12</div>
              </div>
              <div className="dashboard-insight-card dashboard-insight-card--active">
                <h3 style={{ fontSize: '0.875rem', margin: '0 0 0.5rem 0', fontWeight: '500' }}>Ratings</h3>
                <div className="dashboard-insight-value" style={{ fontSize: '1.5rem', fontWeight: '700' }}>24</div>
              </div>
            </div>
          </section>

          <section className="dashboard-notifications">
            <h2 className="dashboard-section-title">Notifications</h2>
            <div className="dashboard-notifications-list">
              {notifications.map(notification => (
                <div key={notification.id} className="dashboard-notification">
                  <Bell size={16} style={{ color: '#ff4d4d', flexShrink: 0 }} />
                  <div className="dashboard-notification-content">
                    <p style={{ margin: '0', fontSize: '0.875rem', fontWeight: '500' }}>
                      {notification.message}
                    </p>
                    <span className="dashboard-notification-time">{notification.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
